import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import MobileReaderChrome from './MobileReaderChrome';
import { PITCH_BLACK_THEME_ID } from '../../reader/readerConstants';
import useReaderAffiliatePromo from '../../hooks/useReaderAffiliatePromo';

const SNAPSHOT_BATCH = 10;

function mergeSnapshotsBatch(start, list) {
  const batch = {};
  (list || []).forEach((snap, i) => {
    batch[start + i] = snap;
  });
  return batch;
}

export default function MobilePdfReader({ bookId }) {
  const { request } = useApi();
  const navigate = useNavigate();
  const userNavigatedRef = useRef(false);
  const { recordForwardFlip, promoElement } = useReaderAffiliatePromo('mobile_pdf_reader', {
    flipsPerPromo: 8,
  });

  const [bookName, setBookName] = useState('');
  const [totalSnapshots, setTotalSnapshots] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [snapshotsByIndex, setSnapshotsByIndex] = useState({});
  const [backgroundThemes, setBackgroundThemes] = useState([]);
  const [selectedBackgroundTheme, setSelectedBackgroundTheme] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSnapshotRange = useCallback(
    async (start, end) => {
      const data = await request({
        method: 'get',
        url: `/api/v1/pdf_parser/snapshots/${bookId}/`,
        params: { start, end },
      });
      if (data?.snapshots?.length) {
        setSnapshotsByIndex((prev) => ({
          ...prev,
          ...mergeSnapshotsBatch(start, data.snapshots),
        }));
      }
    },
    [bookId, request],
  );

  const updateUserBookDetail = useCallback(
    async ({ theme_id, last_read_snapshot } = {}) => {
      if (!localStorage.getItem('access_token')) return;
      const payload = {};
      if (theme_id !== undefined) payload.theme_id = theme_id;
      if (last_read_snapshot !== undefined) payload.last_read_snapshot = last_read_snapshot;
      if (Object.keys(payload).length === 0) return;
      try {
        await request({
          method: 'patch',
          url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
          withAuth: true,
          data: payload,
        });
      } catch (err) {
        console.error('Mobile reader progress save error:', err);
      }
    },
    [bookId, request],
  );

  useEffect(() => {
    let active = true;

    (async () => {
      setInitialLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('access_token');
        let userId = 'guest';
        if (token) {
          userId = localStorage.getItem('user_id') || 'guest';
        }

        const themeData = await request({
          method: 'get',
          url: `/api/v1/pdf_parser/themes/${userId}/`,
        });
        const bt = (themeData?.book_themes || []).filter(
          (t) => t.theme_meta_unique_id !== PITCH_BLACK_THEME_ID,
        );
        if (!active) return;
        setBackgroundThemes(bt);
        setSelectedBackgroundTheme(bt[0] || null);

        let resumeIndex = 0;
        let snapTotal = 0;
        let name = '';

        if (token) {
          try {
            const userData = await request({
              method: 'get',
              url: `/api/v1/pdf_parser/my_book_view/${bookId}/`,
              withAuth: true,
            });
            const bd = userData?.book_detail;
            if (bd) {
              name = bd.book_name || '';
              snapTotal = parseInt(String(bd.original_pages_count), 10) || 0;
              const rawLrs = bd.last_read_snapshot;
              const parsed =
                rawLrs != null && rawLrs !== '' ? parseInt(String(rawLrs), 10) : 0;
              resumeIndex = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);

              const tid =
                typeof bd.theme_id === 'object' && bd.theme_id !== null
                  ? bd.theme_id.theme_meta_unique_id || bd.theme_id.theme_id
                  : bd.theme_id;
              if (tid && bt.length) {
                const matched = bt.find((t) => t.theme_meta_unique_id === tid);
                if (matched) setSelectedBackgroundTheme(matched);
              }
            }
          } catch {
            /* fall through to book detail */
          }
        }

        if (!snapTotal) {
          const bookData = await request({
            method: 'get',
            url: `/api/v1/pdf_parser/books/${bookId}/`,
            params: { start: 0, end: 0 },
          });
          const b = bookData?.book;
          if (!b) {
            if (active) setError('not_found');
            return;
          }
          name = name || b.book_name || '';
          snapTotal = parseInt(String(b.original_pages_count), 10) || 0;
        }

        if (!active) return;
        setBookName(name);
        setTotalSnapshots(snapTotal);
        const safeResume = snapTotal > 0 ? Math.min(resumeIndex, snapTotal - 1) : 0;
        setCurrentIndex(safeResume);

        if (snapTotal > 0) {
          const start = Math.max(0, safeResume - 2);
          const end = Math.min(snapTotal - 1, safeResume + SNAPSHOT_BATCH);
          await fetchSnapshotRange(start, end);
        }
      } catch (err) {
        console.error('Mobile PDF reader init error:', err);
        if (active) setError('load_failed');
      } finally {
        if (active) setInitialLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [bookId, request, fetchSnapshotRange]);

  useEffect(() => {
    if (initialLoading || totalSnapshots === 0) return;

    const snap = snapshotsByIndex[currentIndex];
    if (snap?.snap_shot_url) return;

    let cancelled = false;
    (async () => {
      setPageLoading(true);
      try {
        const start = Math.max(0, currentIndex - 2);
        const end = Math.min(totalSnapshots - 1, currentIndex + SNAPSHOT_BATCH);
        await fetchSnapshotRange(start, end);
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentIndex, initialLoading, totalSnapshots, snapshotsByIndex, fetchSnapshotRange]);

  useEffect(() => {
    if (initialLoading || !userNavigatedRef.current) return;
    void updateUserBookDetail({ last_read_snapshot: currentIndex });
  }, [currentIndex, initialLoading, updateUserBookDetail]);

  const backgroundStyle = useMemo(() => {
    const url = selectedBackgroundTheme?.theme_url;
    if (!url) {
      return { backgroundColor: '#f8fafc' };
    }
    return {
      backgroundImage: `url(${url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
    };
  }, [selectedBackgroundTheme]);

  const currentSnapshot = snapshotsByIndex[currentIndex];
  const canPrev = currentIndex > 0;
  const canNext = totalSnapshots > 0 && currentIndex < totalSnapshots - 1;

  const goPrev = () => {
    if (!canPrev) return;
    userNavigatedRef.current = true;
    setCurrentIndex((i) => i - 1);
  };

  const goNext = () => {
    if (!canNext) return;
    userNavigatedRef.current = true;
    setCurrentIndex((i) => i + 1);
    recordForwardFlip();
  };

  const jumpToPage = useCallback(
    (physicalPage1Based) => {
      if (!totalSnapshots) return;
      const nextIndex = Math.max(
        0,
        Math.min(totalSnapshots - 1, physicalPage1Based - 1),
      );
      if (nextIndex === currentIndex) return;
      userNavigatedRef.current = true;
      setCurrentIndex(nextIndex);
    },
    [currentIndex, totalSnapshots],
  );

  const handleThemeChange = (theme) => {
    setSelectedBackgroundTheme(theme);
    void updateUserBookDetail({ theme_id: theme.theme_meta_unique_id });
  };

  if (initialLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-violet-800 border-t-transparent" />
      </div>
    );
  }

  if (error === 'not_found') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-700">This book could not be found.</p>
        <button
          type="button"
          onClick={() => navigate('/')}
          className="rounded-lg bg-violet-900 px-4 py-2 text-white"
        >
          Go home
        </button>
      </div>
    );
  }

  if (error === 'load_failed') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-gray-700">Could not load this book. Try again later.</p>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg bg-gray-200 px-4 py-2 text-gray-800"
        >
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <MobileReaderChrome
        onBack={() => navigate(-1)}
        pageIndex={currentIndex}
        totalPages={totalSnapshots}
        onPrev={goPrev}
        onNext={goNext}
        onJumpToPage={jumpToPage}
        canPrev={canPrev}
        canNext={canNext}
        backgroundThemes={backgroundThemes}
        selectedThemeId={selectedBackgroundTheme?.theme_meta_unique_id}
        onThemeChange={handleThemeChange}
        backgroundStyle={backgroundStyle}
      >
        <div className="w-full max-w-3xl">
          {pageLoading && !currentSnapshot?.snap_shot_url ? (
            <div className="flex w-full justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-violet-800 border-t-transparent" />
            </div>
          ) : currentSnapshot?.snap_shot_url ? (
            <img
              src={currentSnapshot.snap_shot_url}
              alt={`Page ${currentIndex + 1}`}
              className="w-full max-w-3xl rounded-md bg-white shadow-md"
            />
          ) : (
            <p className="py-16 text-center text-gray-600">Page unavailable.</p>
          )}
        </div>
      </MobileReaderChrome>
      {promoElement}
    </div>
  );
}
